/**
 * AdminPDFTemplates
 *
 * Admin page for managing PDF form templates.
 * Allows admins to upload PDF templates, define field positions, and manage templates.
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from './App';
import { authFetch, fetchCsrfToken } from './authFetch';
import PDFTemplateEditor from './components/PDFTemplateEditor';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function AdminPDFTemplates() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // New template form state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplatePdf, setNewTemplatePdf] = useState(null);
  const [saving, setSaving] = useState(false);

  // Editor modal state
  const [editingTemplate, setEditingTemplate] = useState(null);

  const isAdmin =
    !!(currentUser?.is_staff) ||
    (currentUser?.groups || []).some((g) => (g?.name || '').toLowerCase() === 'admin');

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await authFetch(`${BASE_URL}/api/form-templates/`);
      if (response.ok) {
        const data = await response.json();
        // Handle paginated response (DRF returns {results: [...]} or plain array)
        setTemplates(data.results || data || []);
      } else {
        setError('Failed to fetch templates');
      }
    } catch (e) {
      setError('Error fetching templates: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchTemplates();
    } else {
      setLoading(false);
    }
  }, [isAdmin, fetchTemplates]);

  // Create new template
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplateName.trim()) {
      setError('Template name is required');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      await fetchCsrfToken();

      // Step 1: Create the template
      const createResponse = await authFetch(`${BASE_URL}/api/form-templates/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newTemplateName.trim(),
          description: newTemplateDescription.trim(),
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to create template (${createResponse.status})`);
      }

      const newTemplate = await createResponse.json();

      // Step 2: Upload PDF if provided
      if (newTemplatePdf) {
        const formData = new FormData();
        formData.append('pdf_file', newTemplatePdf);

        const uploadResponse = await authFetch(
          `${BASE_URL}/api/form-templates/${newTemplate.id}/upload_pdf/`,
          {
            method: 'POST',
            body: formData,
          }
        );

        if (!uploadResponse.ok) {
          setMessage('Template created but PDF upload failed. You can upload it later.');
        }
      }

      setMessage('Template created successfully!');
      setShowNewForm(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      setNewTemplatePdf(null);
      fetchTemplates();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle template active status
  const handleToggleActive = async (template) => {
    try {
      await fetchCsrfToken();
      const response = await authFetch(`${BASE_URL}/api/form-templates/${template.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: !template.is_active,
        }),
      });

      if (response.ok) {
        fetchTemplates();
      } else {
        setError('Failed to update template status');
      }
    } catch (e) {
      setError('Error updating template: ' + e.message);
    }
  };

  // Delete template
  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await fetchCsrfToken();
      const response = await authFetch(`${BASE_URL}/api/form-templates/${template.id}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage('Template deleted successfully');
        fetchTemplates();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to delete template');
      }
    } catch (e) {
      setError('Error deleting template: ' + e.message);
    }
  };

  // Upload PDF to existing template
  const handleUploadPdf = async (template, file) => {
    try {
      await fetchCsrfToken();
      const formData = new FormData();
      formData.append('pdf_file', file);

      const response = await authFetch(
        `${BASE_URL}/api/form-templates/${template.id}/upload_pdf/`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (response.ok) {
        setMessage('PDF uploaded successfully');
        fetchTemplates();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to upload PDF');
      }
    } catch (e) {
      setError('Error uploading PDF: ' + e.message);
    }
  };

  // Handle editor save
  const handleEditorSave = async (updatedFieldDefinitions) => {
    if (!editingTemplate) return;

    try {
      await fetchCsrfToken();
      const response = await authFetch(`${BASE_URL}/api/form-templates/${editingTemplate.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field_definitions: updatedFieldDefinitions,
        }),
      });

      if (response.ok) {
        setMessage('Field definitions saved successfully');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to save field definitions');
      }
    } catch (e) {
      setError('Error saving field definitions: ' + e.message);
    }
  };

  // Permission check
  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">PDF Form Templates</h1>
        <p className="mt-2 text-red-600">You don't have permission to manage templates. Admin role required.</p>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-4">PDF Form Templates</h1>
        <div className="text-gray-600">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">PDF Form Templates</h1>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          {showNewForm ? 'Cancel' : '+ New Template'}
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700">
          {message}
          <button onClick={() => setMessage('')} className="float-right text-green-500 hover:text-green-700">×</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Info box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold text-blue-800">How PDF Templates Work:</h3>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>1. Create a template and upload a PDF file (Florida Blue forms)</li>
          <li>2. Click "Edit Fields" to open the visual editor and position form fields</li>
          <li>3. Fields can be text inputs, dates, checkboxes, or signature captures</li>
          <li>4. Map fields to customer data for auto-population</li>
          <li>5. Activate templates to make them available to users</li>
        </ul>
      </div>

      {/* New Template Form */}
      {showNewForm && (
        <div className="mb-6 border rounded-lg p-4 bg-white shadow">
          <h2 className="text-lg font-semibold mb-4">Create New Template</h2>
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Template Name *</label>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="e.g., Florida Blue U65 Application"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                className="w-full border rounded px-3 py-2"
                rows={2}
                placeholder="Optional description of this form..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">PDF File</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setNewTemplatePdf(e.target.files?.[0] ?? null)}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">You can also upload the PDF later</p>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className={`px-4 py-2 rounded text-white ${saving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {saving ? 'Creating...' : 'Create Template'}
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      <div className="space-y-4">
        {templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg border">
            No templates yet. Click "New Template" to create one.
          </div>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className={`border rounded-lg p-4 bg-white shadow ${!template.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{template.name}</h3>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        template.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                  )}
                  <div className="text-xs text-gray-500 mt-2 space-x-4">
                    <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                    {template.created_by_name && <span>By: {template.created_by_name}</span>}
                    <span>
                      Fields: {
                        (template.field_definitions?.pages || []).reduce(
                          (acc, page) => acc + (page.fields?.length || 0),
                          0
                        )
                      }
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* PDF Status */}
                  {template.pdf_url ? (
                    <a
                      href={template.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:underline"
                    >
                      View PDF
                    </a>
                  ) : (
                    <label className="cursor-pointer text-sm text-orange-600 hover:underline">
                      Upload PDF
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadPdf(template, file);
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t flex flex-wrap gap-2">
                <button
                  onClick={() => setEditingTemplate(template)}
                  disabled={!template.pdf_url}
                  className={`px-3 py-1 rounded text-sm ${
                    template.pdf_url
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                  title={!template.pdf_url ? 'Upload a PDF first' : 'Edit field positions'}
                >
                  Edit Fields
                </button>
                <button
                  onClick={() => handleToggleActive(template)}
                  className={`px-3 py-1 rounded text-sm ${
                    template.is_active
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  {template.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(template)}
                  className="px-3 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Template Editor Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-7xl h-[90vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                Edit Fields: {editingTemplate.name}
              </h2>
              <button
                onClick={() => setEditingTemplate(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <PDFTemplateEditor
                template={editingTemplate}
                onSave={handleEditorSave}
                onCancel={() => setEditingTemplate(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
